import { forwardRef } from 'react'

import { ArrowRight01Icon, RefreshIcon } from '@hugeicons/react'
import { cva } from 'class-variance-authority'
import clsx from 'clsx'
import { isMobileOnly } from 'react-device-detect'

import { AppIcon } from '@/shared/ui/icons'
import { Link } from '@/shared/ui/Link'

// ─── Variants ────────────────────────────────────────────────────────────────

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-[6px]',
    'whitespace-nowrap text-center align-middle leading-none',
    'select-none border-0 no-underline cursor-pointer',
    'transition-[transform,box-shadow] duration-[120ms] ease-out',
    // focus — o anel consome o token MEDIDO, não o preenchimento cru: ele tem
    // piso próprio de 3:1 contra a superfície adjacente, e num evento de cor
    // clara o preenchimento sozinho não o sustentaria.
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)]',
    // disabled
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:!transform-none',
    '[&[aria-disabled=true]]:opacity-50 [&[aria-disabled=true]]:cursor-not-allowed [&[aria-disabled=true]]:!transform-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          // A tinta é medida, não branca por decreto: em evento de cor clara
          // branco sobre a marca reprova o piso de 4.5:1 e o token resolve
          // para escuro. Isso vale para 43% dos eventos gamificados vivos.
          'text-[var(--color-brand-inverse)]',
          // Mistura em oklab, não srgb: em srgb a mesma razão entrega peso
          // diferente conforme o matiz — invisível no roxo, suja num âmbar.
          'bg-[linear-gradient(180deg,color-mix(in_oklab,var(--color-brand)_75%,white)_0%,var(--color-brand)_50%,var(--color-brand-active)_100%)]',
          '[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_8px_24px_color-mix(in_oklab,var(--brand)_35%,transparent)]',
          // hover lift
          'hover:enabled:hover:not-[[aria-disabled=true]]:-translate-y-0.5',
          'hover:enabled:[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_10px_28px_color-mix(in_oklab,var(--brand)_42%,transparent)]',
          // press
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97] active:enabled:![box-shadow:none]',
        ],
        progress: [
          'text-white',
          'bg-[linear-gradient(180deg,#ffb74d_0%,#ff9800_100%)]',
          '[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_8px_24px_rgba(255,152,0,.35)]',
          'hover:enabled:-translate-y-0.5',
          'hover:enabled:[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_10px_28px_rgba(255,152,0,.42)]',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97] active:enabled:![box-shadow:none]',
        ],
        success: [
          'text-white',
          'bg-[linear-gradient(180deg,#4ade80_0%,#22c55e_100%)]',
          '[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_8px_24px_rgba(34,197,94,.3)]',
          'hover:enabled:-translate-y-0.5',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97]',
        ],
        whatsapp: [
          'text-white',
          'bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-whatsapp)_88%,white)_0%,var(--color-whatsapp)_55%,var(--color-whatsapp-active)_100%)]',
          '[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_4px_12px_rgba(34,197,94,.28)]',
          'hover:enabled:-translate-y-0.5',
          'hover:enabled:[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_6px_16px_rgba(34,197,94,.36)]',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97] active:enabled:![box-shadow:none]',
        ],
        'success-ghost': [
          'bg-white text-status-success shadow-none',
          '!border-[1.5px] !border-solid !border-status-success',
          'hover:enabled:-translate-y-0.5 hover:enabled:bg-[#f0fdf4]',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97]',
        ],
        'primary-ghost': [
          'bg-white text-[var(--color-brand)] shadow-none',
          '!border-[1.5px] !border-solid !border-[var(--color-brand)]',
          'hover:enabled:-translate-y-0.5 hover:enabled:bg-[color-mix(in_oklab,var(--color-brand)_6%,white)]',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97]',
        ],
        'danger-ghost': [
          'bg-white text-[var(--color-danger)] shadow-none',
          '!border-[1.5px] !border-solid !border-[var(--color-danger)]',
          'hover:enabled:-translate-y-0.5 hover:enabled:bg-[color-mix(in_srgb,var(--color-danger)_8%,white)]',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97]',
        ],
        danger: [
          'text-white',
          'bg-[linear-gradient(180deg,#f87171_0%,#ef4444_100%)]',
          '[box-shadow:inset_0_1px_0_rgba(255,255,255,.35),0_8px_24px_rgba(246,78,96,.35)]',
          'hover:enabled:-translate-y-0.5',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97]',
        ],
        secondary: [
          'text-gray-800 bg-gray-200 shadow-none',
          'hover:enabled:-translate-y-0.5 hover:enabled:bg-[#ebebeb]',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97]',
        ],
        ghost: [
          'text-[var(--color-brand)] bg-[color-mix(in_oklab,var(--color-brand)_8%,transparent)] shadow-none border-0',
          'hover:enabled:-translate-y-0.5 hover:enabled:bg-[color-mix(in_oklab,var(--color-brand)_14%,transparent)]',
          'active:enabled:translate-y-0.5 active:enabled:scale-[0.97]',
        ],
        link: [
          'text-[var(--color-brand)] bg-transparent border-0 underline shadow-none',
          '!h-auto !px-0 !rounded-none font-[inherit]',
          'hover:enabled:text-[var(--color-brand-active)] hover:enabled:!transform-none',
        ],
        /* Elemento APOIADO no fundo da página (o back do `PageHeader`), não
           flutuante sobre conteúdo — então contorno, não sombra. Ver MASTER §2
           (Depth Strategy) e §7 L6.
           `border-2` e não `border`: a caixa tem 30–48px e 1px desaparece
           nessa escala; a espessura acompanha o perímetro. */
        light: [
          'text-gray-700 bg-white border-2 border-solid border-contour',
          'hover:enabled:border-gray-300',
          'hover:enabled:!transform-none',
          'active:enabled:!transform-none active:enabled:!scale-100',
          'transition-colors duration-150 ease-out',
        ],
      },
      size: {
        sm: [
          'hover:enabled:-translate-y-px',
          'active:enabled:translate-y-px',
        ],
        md: [],
        lg: [],
      },
      icon: {
        true: [
          '!p-0 gap-0 shrink-0 !min-h-0 !min-w-0',
          'hover:enabled:!transform-none',
          'active:enabled:!transform-none active:enabled:!scale-100',
          // Área de toque de 44×44 sem alterar a caixa visual (ACCESSIBILITY.md
          // §3): o pseudo-elemento expande só a região que responde ao toque.
          // Usar `min-h-11` empurraria o layout de toda tela que usa botão-ícone
          // e transformaria isto numa change de telas.
          'relative after:absolute after:left-1/2 after:top-1/2',
          'after:h-[var(--target-min)] after:w-[var(--target-min)]',
          'after:-translate-x-1/2 after:-translate-y-1/2 after:content-[""]',
        ],
        false: [],
      },
    },
    compoundVariants: [
      {
        icon: false,
        // `sm` tem 30px de altura visual e é o CTA dos cards de jornada e
        // missão — fluxo principal, onde a meta de 44px é obrigatória. A
        // largura já passa (o rótulo garante), então a expansão é só vertical.
        // `md` (44px) e `lg` (52px) já atendem e não precisam de pseudo.
        size: 'sm',
        class:
          'h-[var(--btn-h-sm)] px-[var(--btn-px-sm)] rounded-[var(--btn-r-sm)] text-[length:var(--btn-fs-sm)] font-[number:var(--btn-fw-sm)] ' +
          'relative after:absolute after:inset-x-0 after:top-1/2 after:h-[var(--target-min)] after:-translate-y-1/2 after:content-[""]',
      },
      {
        icon: false,
        size: 'md',
        class:
          'h-[var(--btn-h-md)] px-[var(--btn-px-md)] rounded-[var(--btn-r-md)] text-[length:var(--btn-fs-md)] font-[number:var(--btn-fw-md)]',
      },
      {
        icon: false,
        size: 'lg',
        class:
          'h-[var(--btn-h-lg)] px-[var(--btn-px-lg)] rounded-[var(--btn-r-lg)] text-[length:var(--btn-fs-lg)] font-[number:var(--btn-fw-lg)]',
      },
      {
        icon: true,
        size: 'sm',
        class: '!w-[30px] !h-[30px] rounded-[0.625rem] [&_svg]:size-4',
      },
      {
        icon: true,
        size: 'md',
        class: '!w-[34px] !h-[34px] rounded-[0.625rem] [&_svg]:size-[18px]',
      },
      {
        icon: true,
        size: 'lg',
        class: '!w-[38px] !h-[38px] rounded-xl [&_svg]:size-5',
      },
    ],
    defaultVariants: {
      size: 'md',
      icon: false,
    },
  }
)

// ─── Trailing icon defaults (per handoff matrix) ──────────────────────────────

const ARROW_VARIANTS = new Set(['primary', 'progress', 'success', 'danger'])

function resolveTrailingIcon(variant, size) {
  const iconSize = size === 'sm' ? 14 : 18
  if (ARROW_VARIANTS.has(variant))
    return <ArrowRight01Icon size={iconSize} variant="stroke" aria-hidden />
  if (variant === 'success-ghost')
    return <RefreshIcon size={size === 'sm' ? 12 : 16} variant="stroke" aria-hidden />
  return null
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <span className="absolute inset-0 flex items-center justify-center text-white" aria-hidden>
      <span
        className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
        role="status"
      />
    </span>
  )
}

// ─── Button ───────────────────────────────────────────────────────────────────

/**
 * CTA gamificado — gradiente vertical + glow de sombra colorida por variante,
 * lift no hover (-translate-y) e press achatado (scale 0.97). A cor não é
 * decorativa: `primary` (roxo) = ação principal, `progress` (laranja) = retomar
 * algo em andamento, `success`/`success-ghost` (verde) = já concluído, revisitar,
 * `danger`/`danger-ghost` (vermelho) = ação destrutiva (sólido = alta ênfase,
 * ghost = borda leve p/ ícones/sair). `size` controla altura/radius/tipografia via
 * tokens `--btn-*`; `icon={true}` vira botão quadrado só-ícone.
 * Sem `variant`, cai no modo legado (wrapper mínimo p/ slug `AppIcon`).
 *
 * @example
 * <Button variant="progress" size="sm">Continuar</Button>
 * <Button variant="success-ghost" size="sm" trailingIcon={<RefreshIcon size={12} />}>Revisitar</Button>
 */
export const Button = forwardRef(function Button(
  {
    href,
    className,
    variant,
    // Sem default aqui de propósito: `size === undefined` precisa distinguir
    // "o consumidor não passou" de "passou md". É o que permite o caminho
    // legado avisar que descartou a prop sem acusar quem nunca a passou. O
    // default vive no caminho semântico, onde a prop é de fato consumida.
    size,
    loading = false,
    leadingIcon,
    trailingIcon,
    showTrailingIcon = true,
    // legacy: AppIcon slug (string) — semantic: icon-only square button (true)
    icon,
    iconClassName = '',
    iconSpin = false,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref
) {
  const isSemantic = variant != null && variant !== ''
  const isIconOnly = icon === true
  const legacyIconSlug = typeof icon === 'string' ? icon : undefined

  // ── Legacy path (no variant): minimal layout wrapper ──────────────────────
  if (!isSemantic) {
    // O caminho legado não lê `size` — nunca leu. O que mudou é que ele deixa
    // de descartar a prop em silêncio: sem aviso, o sintoma que chega ao
    // desenvolvedor é um botão sem altura ao lado de um campo de 44px, e a
    // causa só aparece inspecionando o HTML renderizado.
    if (import.meta.env.DEV && size !== undefined) {
      console.warn(
        `[Button] a prop \`size="${size}"\` foi descartada: sem \`variant\`, o componente ` +
          'cai no caminho legado, que não aplica a escala de controle. ' +
          'Passe `variant` para que `size` valha.'
      )
    }

    const legacyClasses = clsx(
      'flex items-center',
      children && 'gap-2',
      legacyIconSlug && isMobileOnly && 'w-9 h-9 rounded-[0.625rem] p-0',
      className
    )

    const content = legacyIconSlug ? (
      <>
        <AppIcon
          name={legacyIconSlug}
          className={iconClassName}
          size={18}
          spin={iconSpin || iconClassName.includes('icon-spin')}
        />
        <span className="hidden md:block">{children}</span>
      </>
    ) : children

    if (href)
      return (
        <Link href={href} ref={ref} className={legacyClasses} aria-disabled={disabled || undefined} {...rest}>
          {content}
        </Link>
      )

    return (
      <button ref={ref} type={type} disabled={disabled} className={legacyClasses} {...rest}>
        {content}
      </button>
    )
  }

  // ── Semantic path ──────────────────────────────────────────────────────────
  // Aqui, sim, `size` tem default — é o caminho que consome a prop.
  const resolvedSize = size ?? 'md'

  const trailing = (() => {
    if (loading || !showTrailingIcon || isIconOnly) return null
    if (trailingIcon !== undefined) return trailingIcon
    return resolveTrailingIcon(variant, resolvedSize)
  })()

  const classes = clsx(
    buttonVariants({ variant, size: resolvedSize, icon: isIconOnly }),
    loading && 'relative !text-transparent pointer-events-none',
    className
  )

  // Children NÃO são embrulhados em `<span>`. O wrapper era filho direto de um
  // container `inline-flex` e portanto blockificado (`display: block`): o
  // `gap-[6px]` do botão nunca se aplicava, porque havia um único filho, e um
  // fragmento com ícone + texto quebrava em duas linhas que, sob `leading-none`
  // numa caixa de 44px, se sobrepunham. Era a colisão dos CTAs de login social.
  // Como itens de flex diretos, ícone e rótulo participam do gap e do
  // items-center que a classe do botão já anunciava.
  const content = (
    <>
      {loading && <Spinner />}
      {!loading && leadingIcon}
      {!loading && children}
      {!loading && trailing}
    </>
  )

  const sharedProps = {
    className: classes,
    'aria-busy': loading || undefined,
    ...rest,
  }

  if (href)
    return (
      <Link href={href} ref={ref} aria-disabled={disabled || undefined} {...sharedProps}>
        {content}
      </Link>
    )

  return (
    <button ref={ref} type={type} disabled={disabled || loading} {...sharedProps}>
      {content}
    </button>
  )
})

Button.displayName = 'Button'
